/**
 * Keyword drawer ("Refine by topic"), docs/concepts/concept_curated_keywords.md §10.4.
 *
 * Lists the curated keywords of the current result set with their counts.
 * Checking keywords narrows the results on the client (AND); nothing is
 * searched again. On wide screens the drawer sits beside the results, below
 * TOPIC_DRAWER_PUSH_QUERY it overlays them with a scrim.
 */

import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import ListItemButton from '@mui/material/ListItemButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { topicOptions } from '../../searchCore/keywords';
import type { KeywordNavigation } from '../../types/search';
import { APP_BAR_HEIGHT, TOPIC_DRAWER_WIDTH } from './topicDrawerLayout';

// Theme buttons have 10px 24px padding; with it the German labels wrap in the drawer head.
const HEADER_BUTTON_SX = { textTransform: 'none', whiteSpace: 'nowrap', px: 1.5, py: 0.75 } as const;

interface KeywordDrawerProps {
  open: boolean;
  /** Beside the results (wide screens) instead of an overlay with scrim. */
  push: boolean;
  navigation: KeywordNavigation;
  selected: string[];
  onToggle: (keywordId: string) => void;
  onClear: () => void;
  onClose: () => void;
}

export function KeywordDrawer({ open, push, navigation, selected, onToggle, onClear, onClose }: KeywordDrawerProps) {
  const { t, i18n } = useTranslation('search');
  const options = useMemo(
    () => topicOptions(navigation, selected, i18n.language),
    [navigation, selected, i18n.language]
  );

  // The overlay closes on Escape by itself (modal); the pushed drawer needs a listener.
  useEffect(() => {
    if (!open || !push) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, push, onClose]);

  return (
    <Drawer
      variant={push ? 'persistent' : 'temporary'}
      anchor="left"
      open={open}
      onClose={onClose}
      // Stays mounted while closed, so the list keeps its scroll position.
      ModalProps={{ keepMounted: true }}
      slotProps={{
        paper: {
          sx: {
            width: TOPIC_DRAWER_WIDTH,
            maxWidth: '100vw',
            top: { xs: 0, sm: APP_BAR_HEIGHT },
            height: { xs: '100%', sm: `calc(100% - ${APP_BAR_HEIGHT}px)` },
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.default',
          },
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, py: 1 }}>
        <Button startIcon={<ChevronLeftIcon />} onClick={onClose} sx={HEADER_BUTTON_SX}>
          {t('topics.hide')}
        </Button>
        {selected.length > 0 && (
          <Button size="small" onClick={onClear} sx={HEADER_BUTTON_SX}>
            {t('topics.clear')}
          </Button>
        )}
      </Box>
      <Divider />
      <Box role="group" aria-label={t('topics.listLabel')} sx={{ flex: 1, overflowY: 'auto', py: 0.5 }}>
        {options.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
            {t('topics.empty')}
          </Typography>
        ) : (
          options.map((option) => (
            <Tooltip key={option.id} title={option.label} placement="right" enterDelay={600}>
              <ListItemButton
                dense
                role="checkbox"
                aria-checked={option.selected}
                onClick={() => onToggle(option.id)}
                sx={{ height: 40, px: 1.5, gap: 0.5 }}
              >
                <Checkbox size="small" checked={option.selected} tabIndex={-1} disableRipple sx={{ p: 0.5 }} />
                <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                  {option.label}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  {option.count}
                </Typography>
              </ListItemButton>
            </Tooltip>
          ))
        )}
      </Box>
    </Drawer>
  );
}
